import { DOCUMENT, formatDate } from '@angular/common';
import { Component, ElementRef, EventEmitter, Inject, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { Store } from '@ngxs/store'
import { NgxUiLoaderService } from "ngx-ui-loader"
import { Subscription, throwError } from 'rxjs';
import { catchError, filter, finalize, mergeMap } from 'rxjs/operators';
import {
  DynamicScriptLoaderService, FileExportService, ToastService, ValidationService, AccountService
} from '../../../core/services';

import { environment } from '../../../../environments/environment';
import { AddressModalComponent, GiftShareValidators, NAME_REGEXP } from '../../../shared';
import { AuthActions, StaticDataActions } from '../../../state';
import { Address, AddressType, City, UserProfile, UserProfileUpdate } from '../../../core/models';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { isCorporateUser, userImage } from '../../../core/helpers';

declare var jsPDF: any;

@Component({
    selector: 'gs-profile-edit',
    templateUrl: './profile-edit.component.html',
    styleUrls: ['./profile-edit.component.scss'],
    standalone: false
})
export class ProfileEditComponent implements OnInit, OnDestroy {
  @ViewChild('pdfExportView') pdfExportViewElement: ElementRef;

  @Output() done = new EventEmitter<'accept' | 'reject' | 'cancel'>();

  readonly isCorporateUser = isCorporateUser;

  currentDate: Date = new Date();

  user: UserProfile;

  hobbies: any[] = [];
  cities: City[] = [];
  editedImage: string = '';
  userDefaultImageUrl: string = environment.userDefaultImageUrl;
  totalTransaction: number = 0;
  pdfScriptsLoaded: boolean = false;

  form: FormGroup = this.formBuilder.group({
    firstName: [null, [Validators.required, Validators.maxLength(50),
    Validators.pattern(NAME_REGEXP)]],
    lastName: [null, [Validators.required, Validators.maxLength(50),
    Validators.pattern(NAME_REGEXP)]],
    email: [null, [Validators.required, Validators.maxLength(256), GiftShareValidators.email]],
    birthDate: [null, [Validators.required]],
    cityId: [null, [Validators.required]],
    hasNewsletter: [false, [Validators.required]],
    phone: [null, [Validators.maxLength(15)]],
    gender: [null],
    hobbyIds: [[]],
    // description: [null, [Validators.maxLength(1000)]],
  });

  private subscriptions: Subscription[] = [];

  get userImageUrl(): string {
    return userImage(this.user?.image);
  }

  get birthDateFormatted(): string {
    return this.form.value.birthDate ? formatDate(this.form.value.birthDate, 'dd/MM/yyyy', 'en') : null;
  }

  get hobbiesFormatted(): string {
    return this.form.value.hobbyIds != null
    ? this.hobbies.filter(x => this.form.value.hobbyIds.indexOf(x.id) >= 0).map(x => x.name).join()
    : '';
  }

  get genderFormatted(): string {
    return this.form.value.gender == 1 ? 'Masculin' : this.form.value.gender == 2 ? 'Feminin' : null;
  }

  get personalAddress(): Address {
    return this.user?.addresses.find(x => x.type === AddressType.Personal);
  }

  get companyAddress(): Address {
    return this.user?.addresses.find(x => x.type === AddressType.Company);
  }

  constructor(
    private accountService: AccountService,
    private toastService: ToastService,
    private ngxUiLoaderService: NgxUiLoaderService,
    private fileExportService: FileExportService,
    private formBuilder: FormBuilder,
    private store: Store,
    private dynamicScriptLoaderService: DynamicScriptLoaderService,
    private modalService: NgbModal,
    public vs: ValidationService,
    @Inject(DOCUMENT) private document: Document
  ) { }

  ngOnInit() {
    this.store.dispatch([
      new StaticDataActions.GetHobbies()
    ]);

    this.subscriptions.push(
      this.dynamicScriptLoaderService
        .loadMultipleScriptsByName('jspdf', 'xlsx')
        .pipe(mergeMap(() => this.dynamicScriptLoaderService
          .loadScriptByName('jspdf.autotable')))
        .subscribe(() => {
          this.pdfScriptsLoaded = true;
        })
    );

    this.subscriptions.push(
      this.store.select<UserProfile>(state => state.auth.user)
        .subscribe(user => {
          this.user = user;

          if (this.user) {
            if (isCorporateUser(user)) {
              this.form.controls.birthDate.disable();
              this.form.controls.cityId.clearValidators();
              this.form.controls.cityId.updateValueAndValidity();
              this.form.controls.lastName.clearValidators();
              this.form.controls.lastName.updateValueAndValidity();
            }

            this.form.patchValue({
              ...this.user,
              hobbyIds: this.user.hobbies?.map(x => x.id)
            });

            this.editedImage = userImage(this.user.image);
          }
        }),

      this.store.select<any[]>(state => state.staticData.hobbies)
        .pipe(filter(x => !!x))
        .subscribe((response: any) => {
          this.hobbies = response;
        }),

      this.store.select<any[]>(state => state.staticData.cities)
        .pipe(filter(x => !!x))
        .subscribe((response: any) => {
          this.cities = response;
        }),
    );

  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  onDeleteImageClick() {
    this.editedImage = this.userDefaultImageUrl;
  }

  onUploadImageClick(template: TemplateRef<any>) {
    this.modalService.open(template);
  }

  getCity(cityId: number): City {
    return this.cities?.find(x => x.id === cityId);
  }

  /**
   * Submit profile information
   *
   * @returns
   * @memberof ProfileEditComponent
   */
  onSubmit() {
    if (!this.form.valid) {
      this.vs.triggerValidation(this.form);
      return;
    }

    const currentImage = this.editedImage === this.userDefaultImageUrl ? null : this.editedImage.split('/').pop();
    const model = {
      ...this.form.value,
      image: currentImage,
      birthDate: this.form.value.birthDate != null ? formatDate(this.form.value.birthDate, 'yyyy-MM-dd', 'en') : null
    } as UserProfileUpdate;

    this.ngxUiLoaderService.start();
    this.subscriptions.push(
      this.accountService.updateUser(this.user.code, model)
        .pipe(
          catchError(e => {
            this.toastService.show('Eroare', e.error?.detail || e.error?.title || 'A aparut o problema neasteptata. Echipa noastra lucreaza pentru a o remedia.', 'error');
            return throwError(e);
          }),
          finalize(() => {
            this.ngxUiLoaderService.stop();
          })
        )
        .subscribe(() => {
          this.toastService.show('Succes', 'Modificarile au fost salvate cu succes.', 'success');

          this.store.dispatch(new AuthActions.SetUser({
            ...this.user,
            ...this.form.value,
            name: this.form.value.firstName + ' ' + this.form.value.lastName,
            hobbies: this.form.value.hobbyIds?.map(x => this.hobbies.find(h => h.id === x)),
            image: currentImage
          }));
        })
    );
  }

  onlyNumberKey(event) {
    return (event.charCode == 8 || event.charCode == 0) ? null : event.charCode >= 48 && event.charCode <= 57;
  }

  /**
   * Get Image Uploaded File Name.
   * @param {any} data
   * @memberof ProfileEditComponent
   */
  onImageSelected(image) {
    this.editedImage = userImage(image);
  }

  exportAsPdf() {
    this.convertToDataURLviaCanvas(this.userImageUrl, 'image/jpeg')
      .then((data: any) => {
        const imgData = data;
        const doc = new jsPDF();
        doc.setFontSize(40);
        doc.text(35, 25, `Profil ${this.form.value.firstName} ${this.form.value.lastName}`);

        if (imgData != null) {
          doc.addImage(imgData, 'JPEG', 15, 40, 60, 60);
        }

        doc.setTextColor(0, 0, 0);

        const specialElementHandlers = {
          '#bypassme': function (element, renderer) {
            return true;
          }
        };

        const margins = {
          top: 100,
          bottom: 60,
          left: 15,
          width: 522
        };

        doc.fromHTML(
          this.pdfExportViewElement.nativeElement,
          margins.left, // x coord
          margins.top, { // y coord
          'width': margins.width, // max width of content on PDF
          'elementHandlers': specialElementHandlers
        }, function (dispose) {
          doc.save(`profil-giftshare-${formatDate(new Date(), 'yyyy-MM-dd-HH-mm', 'en')}.pdf`);
        }, margins);

      });

  }

  exportAsExcel() {
    const city = this.getCity(this.form.value.cityId);
    const model: any = {
      firstName: this.form.value.firstName,
      lastName: this.form.value.lastName,
      email: this.form.value.email,
      city: city?.nameFormatted ?? '',
      gender: this.genderFormatted,
      birthDate: this.birthDateFormatted,
      phone: this.form.value.phone,
      hobbies: this.hobbiesFormatted
    };

    this.fileExportService.exportAsExcel(
      [model],
      ['firstName', 'lastName', 'email', 'city', 'gender', 'birthDate', 'phone', 'hobbies'],
      ['Prenume', 'Nume', 'Email', 'Localitate', 'Gen', 'Data nasterii', 'Telefon', 'Pasiuni'],
      `profil-giftshare-${formatDate(new Date(), 'yyyy-MM-dd-HH-mm', 'en')}`
    );

  }

  private convertToDataURLviaCanvas(url, outputFormat): Promise<any> {
    let that = this;
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.addEventListener('load', function () {
        let canvas = <HTMLCanvasElement>that.document.createElement('CANVAS');
        const ctx = canvas.getContext('2d');
        canvas.height = img.height;
        canvas.width = img.width;
        ctx.drawImage(img, 0, 0);
        const dataURL = canvas.toDataURL(outputFormat);
        canvas = null;
        resolve(dataURL);
      }, false);
      img.src = url;
    });
  }

  onEditAddress(address: Address = null) {
    address = address ?? { type: AddressType.Company } as Address;

    const modalRef = this.modalService.open(AddressModalComponent, { backdrop: 'static', size: 'lg' });
    modalRef.componentInstance.address = address;
    modalRef.componentInstance.title = address.type == AddressType.Company ? 'Modifica persoana juridica' : 'Modifica persoana fizica';

    // this.subscriptions.push(
    //   modalRef.closed
    //     .subscribe((result: Address) => {
    //       if (result) {
    //         //Persist address?
    //       }
    //     })
    // );
  }
}
